import { MotionReveal } from "../components/MotionReveal";
import { useMemo, useState } from "react";

export default function RoiPage() {
  const [sev1PerMonth, setSev1PerMonth] = useState(6);
  const [downtimeCostPerHour, setDowntimeCostPerHour] = useState(300000);
  const [teamSize, setTeamSize] = useState(18);

  const estimate = useMemo(() => {
    const annualSev1 = Math.max(0, sev1PerMonth) * 12;
    const avgHoursPerSev1 = 2.0;
    const annualDowntimeCost = annualSev1 * avgHoursPerSev1 * Math.max(0, downtimeCostPerHour);

    const downtimeSaved = annualDowntimeCost * 0.45;
    const opsEfficiencySaved = Math.max(0, teamSize) * 180000 * 0.2;
    const toolOptimizationSaved = 350000;

    const total = downtimeSaved + opsEfficiencySaved + toolOptimizationSaved;
    return {
      downtimeSaved,
      opsEfficiencySaved,
      toolOptimizationSaved,
      total,
    };
  }, [downtimeCostPerHour, sev1PerMonth, teamSize]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="pt-6 sm:pt-10">
        <MotionReveal>
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs uppercase tracking-[0.18em] text-[var(--text-secondary)]">
              Executive-grade business case
            </div>
            <h1 className="mt-6 font-[var(--font-display)] text-4xl sm:text-5xl tracking-tight text-white">The ROI of AIOps</h1>
            <p className="mt-4 max-w-3xl text-sm sm:text-base text-[var(--text-secondary)]">
              Intelligent observability pays for itself by reducing downtime, accelerating resolution, and automating repetitive work.
            </p>
          </div>
        </MotionReveal>
      </div>

      <section className="mt-10 sm:mt-14">
        <MotionReveal>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="glass-panel rounded-3xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">Cost of inaction</div>
              <div className="mt-4 grid gap-3 text-sm">
                <CostBlock title="Cost of Downtime" desc="Average: $300K/hour for large enterprises" />
                <CostBlock title="Cost of Manual Operations" desc="60–70% of time spent on firefighting vs innovation" />
                <CostBlock title="Tool Sprawl Inefficiency" desc="Duplicate licenses and integration maintenance burden" />
                <CostBlock title="Alert Fatigue Economics" desc="Most alerts are noise; critical signals get missed" />
              </div>
            </div>

            <div className="glass-panel rounded-3xl p-6 sm:p-8">
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">AIOps ROI metrics</div>
              <div className="mt-4 overflow-hidden rounded-2xl border border-white/10 bg-white/3">
                <div className="grid">
                  {[
                    ["Alert Reduction", "60–80%"],
                    ["MTTR Improvement", "40–70%"],
                    ["Sev-1 Incident Reduction", "50%+"],
                    ["Operational Efficiency", "40%+"],
                    ["Tool Cost Optimization", "20–30%"],
                  ].map(([k, v]) => (
                    <div key={k} className="grid grid-cols-2 gap-3 border-b border-white/8 px-5 py-4 last:border-b-0">
                      <div className="text-sm text-[var(--text-secondary)]">{k}</div>
                      <div className="text-sm font-medium text-white">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <section className="mt-12 sm:mt-16">
        <MotionReveal>
          <div className="glass-panel rounded-3xl p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">ROI calculator (concept)</div>
            <div className="mt-3 font-[var(--font-display)] text-2xl tracking-tight text-white">Calculate your potential ROI</div>
            <div className="mt-6 grid gap-6 lg:grid-cols-2">
              <div className="grid gap-4">
                <NumberField label="Sev-1 incidents per month" value={sev1PerMonth} onChange={setSev1PerMonth} />
                <NumberField label="Average downtime cost per hour (USD)" value={downtimeCostPerHour} onChange={setDowntimeCostPerHour} />
                <NumberField label="Ops / SRE team size" value={teamSize} onChange={setTeamSize} />
                <div className="text-xs text-[var(--text-secondary)]">
                  This is a conservative placeholder model for demonstration. Replace with your enterprise-specific calculator.
                </div>
              </div>

              <div className="rounded-3xl border border-white/10 bg-white/3 p-6">
                <div className="text-sm text-[var(--text-secondary)]">Estimated annual savings</div>
                <div className="mt-3 font-[var(--font-mono)] text-3xl text-white">${format(estimate.total)}</div>
                <div className="mt-6 grid gap-3 text-sm">
                  <Row label="Downtime reduction" value={`$${format(estimate.downtimeSaved)}`} />
                  <Row label="Ops efficiency" value={`$${format(estimate.opsEfficiencySaved)}`} />
                  <Row label="Tool optimization" value={`$${format(estimate.toolOptimizationSaved)}`} />
                </div>
                <a
                  href="/contact"
                  className="mt-6 inline-flex w-full items-center justify-center rounded-xl px-5 py-3 text-sm font-medium text-[#061019] bg-[var(--accent-primary)] hover:brightness-110"
                >
                  Request ROI Assessment
                </a>
              </div>
            </div>
          </div>
        </MotionReveal>
      </section>

      <div className="h-12" />
    </div>
  );
}

function CostBlock({ title, desc }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/3 px-4 py-3">
      <div className="text-sm text-white">{title}</div>
      <div className="mt-1 text-sm text-[var(--text-secondary)]">{desc}</div>
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label className="grid gap-2">
      <span className="text-xs uppercase tracking-[0.22em] text-[var(--text-secondary)]">{label}</span>
      <input
        type="number"
        className="w-full rounded-2xl border border-white/10 bg-white/3 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)]"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div className="text-[var(--text-secondary)]">{label}</div>
      <div className="font-medium text-white">{value}</div>
    </div>
  );
}

function format(n) {
  return Math.round(n).toLocaleString("en-US");
}
